import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronDoubleLeftIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  BellIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';
import { useRotuloRotaAtiva } from './navConfig';
import type { Density } from '../../contexts/theme';

interface TopbarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onAbrirPalette: () => void;
  onAbrirMobileMenu: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);

const Topbar: React.FC<TopbarProps> = ({ collapsed, onToggleCollapsed, onAbrirPalette, onAbrirMobileMenu }) => {
  const { theme, toggleTheme, density, setDensity } = useTheme();
  const rotulo = useRotuloRotaAtiva();

  // Notificações — placeholder (sem fonte de notificações no produto ainda).
  const [notifAberto, setNotifAberto] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!notifAberto) return;
    const fechar = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [notifAberto]);

  const seg = (ativo: boolean) =>
    `rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors duration-150 ${
      ativo ? 'bg-surface text-tx shadow-sm' : 'text-tx-3 hover:text-tx-2'
    }`;

  return (
    <header className="app-chrome flex h-[var(--topbar-h)] flex-shrink-0 items-center gap-3 border-b border-line bg-sidebar-bg px-3 md:px-[18px]">
      {/* Mobile: hamburger abre a gaveta */}
      <button
        onClick={onAbrirMobileMenu}
        className="rounded-iris-sm p-2.5 text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:hidden"
        aria-label="Abrir menu"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      {/* Desktop: recolher sidebar (gira 180° quando recolhida) */}
      <button
        onClick={onToggleCollapsed}
        className="hidden rounded-iris-sm p-1.5 text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:block"
        title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        <ChevronDoubleLeftIcon
          className="h-[17px] w-[17px] transition-transform duration-200"
          style={{ transform: collapsed ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {/* Breadcrumb */}
      <div className="min-w-0 flex items-baseline gap-1.5 text-sm">
        <span className="hidden text-tx-3 sm:inline">UtilyHub</span>
        {rotulo && <span className="hidden text-tx-3 sm:inline">›</span>}
        <span className="truncate font-semibold text-tx">{rotulo || 'Home'}</span>
      </div>

      <div className="flex-1" />

      {/* Pílula de busca → Command Palette */}
      <button
        onClick={onAbrirPalette}
        className="flex h-8 items-center gap-2 rounded-iris-sm border border-line bg-surface-2 px-2.5 text-sm text-tx-3 transition-colors hover:border-line-strong hover:text-tx-2"
        title="Buscar ações e telas (Ctrl+K)"
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="hidden lg:inline">Buscar ações, telas…</span>
        <kbd className="hidden rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[10px] text-tx-3 md:inline">
          {isMac ? '⌘K' : 'Ctrl+K'}
        </kbd>
      </button>

      {/* Densidade */}
      <div className="hidden items-center gap-0.5 rounded-iris-sm bg-surface-2 p-0.5 md:flex">
        {(['comfortable', 'compact'] as Density[]).map((d) => (
          <button key={d} onClick={() => setDensity(d)} className={seg(density === d)}>
            {d === 'comfortable' ? 'Confortável' : 'Compacto'}
          </button>
        ))}
      </div>

      {/* Tema */}
      <button
        onClick={toggleTheme}
        className="hidden h-8 w-8 items-center justify-center rounded-iris-sm text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:flex"
        title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
      >
        {theme === 'dark' ? <SunIcon className="h-[18px] w-[18px]" /> : <MoonIcon className="h-[18px] w-[18px]" />}
      </button>

      {/* Notificações (placeholder — dot accent preparado pra quando houver fonte) */}
      <div className="relative hidden md:block" ref={notifRef}>
        <button
          onClick={() => setNotifAberto((v) => !v)}
          className="flex h-8 w-8 items-center justify-center rounded-iris-sm text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx"
          title="Notificações"
        >
          <BellIcon className="h-[18px] w-[18px]" />
        </button>
        {notifAberto && (
          <div className="absolute right-0 top-full z-50 mt-1 w-72 rounded-iris border border-line bg-surface p-4 shadow-iris-xl">
            <div className="text-sm font-semibold text-tx">Notificações</div>
            <p className="mt-2 text-sm text-tx-3">Sem notificações por enquanto.</p>
          </div>
        )}
      </div>
    </header>
  );
};

export default Topbar;
